/**
 * Team Formation Component
 * Create and manage teams for golf outings.
 */
import React, { useState, useEffect } from 'react';
import { Users, Star, UserPlus, Shuffle, Trash2, Edit2, Check, X, MessageCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { useOutingApi } from '../hooks/useOutingApi';
import { useOuting } from '../context/OutingContext';
import { PAYMENT_STATUS, formatCurrency, generateTeamName } from '../utils/outingHelpers';

export default function TeamFormation({ outingId, user, teamSize, entryFee, onRefresh, onSelectTeamChat }) {
  const api = useOutingApi();
  const { teams, invitations, fetchTeams } = useOuting();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [autoPairing, setAutoPairing] = useState(false);
  const [newTeam, setNewTeam] = useState({
    name: '',
    members: Array(teamSize).fill({ player_name: '', email: '', role: 'player', is_guest: false })
  });

  useEffect(() => {
    fetchTeams(outingId);
  }, [outingId, fetchTeams]);

  const handleCreateTeam = async () => {
    if (!newTeam.name) {
      toast.error('Please enter a team name');
      return;
    }
    
    const validMembers = newTeam.members.filter(m => m.player_name.trim());
    if (validMembers.length === 0) {
      toast.error('Please add at least one team member');
      return;
    }
    
    // Ensure first member is captain
    validMembers[0].role = 'captain';
    
    try {
      await api.createTeam(outingId, {
        name: newTeam.name,
        members: validMembers
      }, user.user_id);
      
      toast.success('Team created!');
      setShowCreateModal(false);
      resetNewTeam();
      fetchTeams(outingId);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAutoPair = async () => {
    setAutoPairing(true);
    try {
      const result = await api.autoPairPlayers(outingId, user.user_id);
      if (result.teams_created > 0) {
        toast.success(`Created ${result.teams_created} teams!`);
        fetchTeams(outingId);
        onRefresh?.();
      } else {
        toast.info('No unpaired players to form teams');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAutoPairing(false);
    }
  };

  const resetNewTeam = () => {
    setNewTeam({
      name: generateTeamName(teams.length),
      members: Array(teamSize).fill({ player_name: '', email: '', role: 'player', is_guest: false })
    });
  };

  const updateMember = (index, field, value) => {
    setNewTeam(prev => ({
      ...prev,
      members: prev.members.map((m, i) => 
        i === index ? { ...m, [field]: value } : m
      )
    }));
  };

  // Count unpaired accepted invitations
  const acceptedInvitations = invitations.filter(i => i.status === 'accepted');
  const assignedEmails = new Set(teams.flatMap(t => t.members?.map(m => m.email) || []));
  const unpaired = acceptedInvitations.filter(i => i.email && !assignedEmails.has(i.email));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white">Teams</h3>
          <p className="text-sm text-zinc-400">{teams.length} teams formed</p>
        </div>
        <div className="flex gap-2">
          {unpaired.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleAutoPair}
              disabled={autoPairing}
              className="border-purple-500 text-purple-400"
              data-testid="auto-pair-btn"
            >
              <Shuffle className="w-4 h-4 mr-2" />
              {autoPairing ? 'Pairing...' : `Auto-Pair (${unpaired.length})`}
            </Button>
          )}
          <Dialog open={showCreateModal} onOpenChange={(open) => {
            setShowCreateModal(open);
            if (open) resetNewTeam();
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#007AFF] hover:bg-[#38BDF8]" data-testid="create-team-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Quick Add Team
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle>Quick Add Team</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <Label>Team Name *</Label>
                  <Input
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    placeholder="The Eagles"
                    className="bg-[#1A1A1A] border-white/20 mt-1"
                    data-testid="team-name-input"
                  />
                </div>
                
                <div className="space-y-3">
                  <Label>Team Members ({teamSize}-person team)</Label>
                  {newTeam.members.map((member, index) => (
                    <Card key={index} className="bg-[#1A1A1A] border-white/10">
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-400">
                            {index === 0 ? (
                              <span className="flex items-center gap-1 text-yellow-400">
                                <Star className="w-3 h-3" /> Captain
                              </span>
                            ) : (
                              `Player ${index + 1}`
                            )}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-zinc-500">Guest?</span>
                            <Switch
                              checked={member.is_guest}
                              onCheckedChange={(v) => updateMember(index, 'is_guest', v)}
                            />
                          </div>
                        </div>
                        <Input
                          value={member.player_name}
                          onChange={(e) => updateMember(index, 'player_name', e.target.value)}
                          placeholder="Player name"
                          className="bg-[#0A0A0A] border-white/10"
                        />
                        {!member.is_guest && (
                          <Input
                            value={member.email}
                            onChange={(e) => updateMember(index, 'email', e.target.value)}
                            placeholder="Email (optional)"
                            className="bg-[#0A0A0A] border-white/10"
                          />
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-300">
                    Entry fee: {formatCurrency(entryFee)} × {newTeam.members.filter(m => m.player_name).length || 0} = 
                    <span className="font-bold ml-1">
                      {formatCurrency(entryFee * (newTeam.members.filter(m => m.player_name).length || 0))}
                    </span>
                  </p>
                </div>
                
                <Button
                  onClick={handleCreateTeam}
                  className="w-full bg-[#007AFF] hover:bg-[#38BDF8]"
                  data-testid="submit-team-btn"
                >
                  Create Team
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Unpaired Notice */}
      {unpaired.length > 0 && (
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-300 text-sm">
                {unpaired.length} accepted player{unpaired.length !== 1 ? 's' : ''} not yet assigned to teams
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Teams List */}
      {teams.length === 0 ? (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h4 className="font-semibold text-white mb-2">No Teams Yet</h4>
            <p className="text-zinc-400 text-sm">Create teams manually or auto-pair accepted players</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {teams.map(team => (
            <TeamCard 
              key={team.id} 
              team={team} 
              entryFee={entryFee}
              onChatClick={onSelectTeamChat ? () => onSelectTeamChat(team) : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TeamCard({ team, entryFee, onChatClick }) {
  const statusConfig = PAYMENT_STATUS[team.payment_status] || PAYMENT_STATUS.pending;
  
  return (
    <Card className="bg-[#121212] border-white/10 hover:border-white/20 transition-colors">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            {team.name}
          </CardTitle>
          <div className="flex items-center gap-2">
            {onChatClick && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onChatClick}
                className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10"
                data-testid={`team-chat-btn-${team.id}`}
              >
                <MessageCircle className="w-4 h-4" />
              </Button>
            )}
            <Badge className={`${statusConfig.bgColor} text-${statusConfig.color}-400`}>
              {statusConfig.label}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Members */}
        <div className="space-y-2">
          {team.members?.map((member, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              {member.role === 'captain' ? (
                <Star className="w-4 h-4 text-yellow-400" />
              ) : (
                <Users className="w-4 h-4 text-zinc-500" />
              )}
              <span className="text-white">{member.player_name}</span>
              {member.is_guest && (
                <Badge variant="outline" className="text-xs">Guest</Badge>
              )}
            </div>
          ))}
        </div>
        
        {/* Payment Info */}
        <div className="pt-2 border-t border-white/10 flex justify-between text-sm">
          <span className="text-zinc-400">Payment</span>
          <span className={`text-${statusConfig.color}-400`}>
            {formatCurrency(team.total_paid || 0)} / {formatCurrency(team.total_due || 0)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
